import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';
import type { AxiosRequestHeaders } from 'axios';
import { msalInstance } from '../../../index';

/**
 * Microsoft Graph specific scopes for different operations
 */
export const MICROSOFT_GRAPH_SCOPES = {
  CONTACTS_READ: 'https://graph.microsoft.com/Contacts.Read',
  CONTACTS_READWRITE: 'https://graph.microsoft.com/Contacts.ReadWrite',
  MAIL_READ: 'https://graph.microsoft.com/Mail.Read',
  MAIL_READWRITE: 'https://graph.microsoft.com/Mail.ReadWrite',
  CALENDAR_READ: 'https://graph.microsoft.com/Calendars.Read',
  CALENDAR_READWRITE: 'https://graph.microsoft.com/Calendars.ReadWrite',
  USER_READ: 'https://graph.microsoft.com/User.Read',
  USER_READWRITE: 'https://graph.microsoft.com/User.ReadWrite',
} as const;

export type MicrosoftGraphScope = typeof MICROSOFT_GRAPH_SCOPES[keyof typeof MICROSOFT_GRAPH_SCOPES];

type GetMicrosoftTokenFn = (scopes: MicrosoftGraphScope[]) => Promise<string | null>;

/**
 * Microsoft Graph token getter using MSAL.
 * It first tries `acquireTokenSilent`, then falls back to `acquireTokenPopup`.
 */
export const getMicrosoftGraphToken: GetMicrosoftTokenFn = async (scopes: MicrosoftGraphScope[]) => {
  // E2E test seam: never attempt interactive MSAL (popup) during automated tests.
  // Guarded by REACT_APP_E2E so it is completely inert in normal and production builds.
  if (process.env.REACT_APP_E2E === 'true') {
    try {
      const injected =
        typeof window !== 'undefined'
          ? window.localStorage.getItem('E2E_GRAPH_TOKEN')
          : null;
      return injected ?? 'e2e-graph-token';
    } catch {
      return 'e2e-graph-token';
    }
  }

  try {
    if (!msalInstance) {
      throw new Error('MSAL instance not available');
    }

    const accounts = msalInstance.getAllAccounts();
    if (!accounts || accounts.length === 0) {
      throw new Error('No authenticated accounts found');
    }

    const silentRequest = {
      scopes,
      account: accounts[0],
    };

    const result = await msalInstance.acquireTokenSilent(silentRequest);
    return result?.accessToken ?? null;
  } catch (silentError) {
    try {
      const interactiveRequest = {
        scopes,
        prompt: 'select_account',
      };

      const result = await msalInstance.acquireTokenPopup(interactiveRequest);
      return result?.accessToken ?? null;
    } catch (interactiveError) {
      console.warn('Could not acquire Microsoft Graph token silently or via popup', interactiveError);
      return null;
    }
  }
};

/**
 * Installs Microsoft Graph specific request/response interceptors.
 * - Adds Authorization header using Microsoft Graph token acquisition
 * - Handles Microsoft Graph specific error responses
 * - Retries requests once after a 401 by obtaining a fresh token
 */
export function setupMicrosoftGraphInterceptors(
  axiosInstance: AxiosInstance,
  scopes: MicrosoftGraphScope[],
  getToken: GetMicrosoftTokenFn = getMicrosoftGraphToken
) {
  const reqInterceptor = axiosInstance.interceptors.request.use(
    async (config: AxiosRequestConfig & { headers?: AxiosRequestHeaders }) => {
      try {
        const token = await getToken(scopes);
        const headers: AxiosRequestHeaders = (config.headers as AxiosRequestHeaders) || {};

        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        // Microsoft Graph specific headers
        headers['Accept'] = 'application/json';
        headers['Content-Type'] = 'application/json';

        config.headers = headers as any;
        return config as any;
      } catch (err) {
        console.error('Error setting up Microsoft Graph request:', err);
        return config as any;
      }
    },
    (error) => Promise.reject(error)
  );

  const resInterceptor = axiosInstance.interceptors.response.use(
    (resp) => resp,
    async (error: AxiosError & { config?: any }) => {
      const originalRequest = error.config;
      if (!originalRequest) return Promise.reject(error);

      // If 401 and we haven't retried yet, attempt to refresh token and retry once
      if (error.response && error.response.status === 401 && !(originalRequest as any)._retry) {
        (originalRequest as any)._retry = true;
        try {
          const token = await getToken(scopes);
          if (token) {
            originalRequest.headers = originalRequest.headers ?? {};
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          }
        } catch (e) {
          // fallthrough to reject
        }
      }

      // Normalize Microsoft Graph errors
      try {
        const respData = error.response?.data as any;

        // Microsoft Graph error format: { error: { code, message, innerError } }
        let message = error.message || 'Unknown Microsoft Graph error';
        let code: string | undefined;
        let details: any;

        if (respData?.error) {
          message = respData.error.message || message;
          code = respData.error.code;
          details = respData.error.innerError || respData.error;
        }

        const normalized: any = new Error(String(message));
        normalized.status = error.response?.status;
        normalized.data = error.response?.data;
        normalized.code = code; // Microsoft Graph specific error code
        normalized.details = details; // Microsoft Graph specific error details

        return Promise.reject(normalized);
      } catch (e) {
        // Fallback to original error if normalization fails
        return Promise.reject(error);
      }
    }
  );

  return () => {
    axiosInstance.interceptors.request.eject(reqInterceptor);
    axiosInstance.interceptors.response.eject(resInterceptor);
  };
}

/**
 * Convenience: create a pre-configured axios instance for Microsoft Graph API.
 * Usage:
 * import { createMicrosoftGraphAxios } from './services/interceptors/microsoftGraphInterceptors';
 * const graphApi = createMicrosoftGraphAxios({}, ['https://graph.microsoft.com/Contacts.Read']);
 */
export function createMicrosoftGraphAxios(
  config: AxiosRequestConfig = {},
  scopes: MicrosoftGraphScope[] = ['https://graph.microsoft.com/User.Read']
) {
  const instance = axios.create({
    timeout: 30000, // Microsoft Graph can be slower than internal APIs
    ...config,
  });

  setupMicrosoftGraphInterceptors(instance, scopes);
  return instance;
}

export default setupMicrosoftGraphInterceptors;